import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import {
  Play, Pause, Upload, Trash2, Check, X, RefreshCcw, Image as ImageIcon,
  Clock, DollarSign, Zap, Mic, FileText,
} from "lucide-react";

const DAYS = [
  { v: 0, l: "Sun" }, { v: 1, l: "Mon" }, { v: 2, l: "Tue" },
  { v: 3, l: "Wed" }, { v: 4, l: "Thu" }, { v: 5, l: "Fri" }, { v: 6, l: "Sat" },
];

export default function GuideAgentAdmin() {
  const [cfg, setCfg] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const fileRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [c, q, l, s] = await Promise.all([
        api.get("/admin/guide-agent/config"),
        api.get("/admin/guide-agent/queue"),
        api.get("/admin/guide-agent/logs?limit=50"),
        api.get("/admin/guide-agent/screens"),
      ]);
      setCfg(c.data);
      setJobs(q.data.jobs || []);
      setLogs(l.data.logs || []);
      setScreens(s.data.screens || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load guide-agent state");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  // Live-ish refresh so a running render shows status progress without a manual tap.
  useEffect(() => {
    const t = setInterval(loadAll, 10000);
    return () => clearInterval(t);
  }, [loadAll]);

  const patchCfg = async (patch) => {
    setWorking(true);
    try {
      await api.patch("/admin/guide-agent/config", patch);
      toast.success("Saved");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setWorking(false);
    }
  };

  const toggleDay = (d) => {
    const cur = new Set(cfg.schedule_days || []);
    cur.has(d) ? cur.delete(d) : cur.add(d);
    patchCfg({ schedule_days: Array.from(cur).sort() });
  };

  const generateNow = async (kind) => {
    setWorking(true);
    try {
      const r = await api.post("/admin/guide-agent/generate-now", { kind });
      toast.success(r.data.duplicate ? "Already queued today" : `Queued ${kind}`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not queue");
    } finally {
      setWorking(false);
    }
  };

  const approve = async (id) => {
    await api.post(`/admin/guide-agent/jobs/${id}/approve`);
    toast.success("Approved + published");
    loadAll();
  };
  const reject = async (id) => {
    const reason = prompt("Reject reason (optional)") || "";
    await api.post(`/admin/guide-agent/jobs/${id}/reject`, { reason });
    toast.success("Rejected");
    loadAll();
  };

  const uploadScreen = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("image", file);
    fd.append("label", file.name.replace(/\.[^.]+$/, "").slice(0, 60));
    try {
      await api.post("/admin/guide-agent/screens", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Screen added");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const deleteScreen = async (id) => {
    if (!window.confirm("Delete this screenshot?")) return;
    await api.delete(`/admin/guide-agent/screens/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  if (loading) return <div className="p-8 font-mono text-white/60">Loading guide agent…</div>;
  if (!cfg) return <div className="p-8 font-mono text-red-500">Could not load config.</div>;

  const spendPct = Math.min(100, Math.round((cfg.month_spend_usd / cfg.monthly_cap_usd) * 100));
  const approvalsNeeded = Math.max(0, (cfg.auto_publish_after_approvals || 2) - (cfg.approvals_completed || 0));

  return (
    <div data-testid="guide-agent-admin" className="min-h-screen bg-[#0A0A0C] text-white p-6 sm:p-10 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.4em] text-[#39FF14]">// matrixvideo</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Guide Agent</h1>
          <p className="text-white/50 text-sm mt-1">Autonomous twice-weekly tutorials and earning explainers posted under <Link to="/u/matrixguide" className="text-[#00FFFF] hover:underline">@matrixguide</Link>.</p>
        </div>
        <button
          data-testid="guide-pause-toggle"
          onClick={() => patchCfg({ paused: !cfg.paused })}
          disabled={working}
          className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border transition-all ${cfg.paused ? "border-[#FF007F] text-[#FF007F] hover:bg-[#FF007F]/10" : "border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10"}`}
        >
          {cfg.paused ? <><Play className="h-3 w-3 inline mr-2" />Resume</> : <><Pause className="h-3 w-3 inline mr-2" />Pause</>}
        </button>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <Card icon={Clock} label="Status" value={cfg.paused ? "PAUSED" : "LIVE"} color={cfg.paused ? "#FF007F" : "#39FF14"} />
        <Card icon={DollarSign} label="This month" value={`$${cfg.month_spend_usd?.toFixed(2) || "0.00"}`} sub={`of $${cfg.monthly_cap_usd} cap`} pct={spendPct} />
        <Card icon={ImageIcon} label="Screens" value={cfg.screens_count || 0} sub="in library" color={cfg.screens_count ? "#00FFFF" : "#FF007F"} />
        <Card icon={Check} label="Approvals" value={cfg.approvals_completed || 0} sub={approvalsNeeded ? `${approvalsNeeded} more → auto-publish` : "Auto-publish active"} color={approvalsNeeded ? "#FFD700" : "#39FF14"} />
      </div>

      {/* Schedule */}
      <section className="mb-10">
        <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-white/70 mb-3">// Schedule</h2>
        <div className="border border-white/10 p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[11px] uppercase tracking-widest text-white/60 mr-3">Fire on</span>
            {DAYS.map((d) => {
              const on = (cfg.schedule_days || []).includes(d.v);
              return (
                <button
                  key={d.v}
                  data-testid={`guide-day-${d.v}`}
                  onClick={() => toggleDay(d.v)}
                  disabled={working}
                  className={`px-3 py-1.5 font-mono text-xs uppercase tracking-widest border transition-all ${on ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/15 text-white/50 hover:border-white/30"}`}
                >{d.l}</button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="font-mono text-[11px] uppercase tracking-widest text-white/60">Time (local to tz)</label>
            <input
              type="time"
              value={`${String(cfg.schedule_hour).padStart(2, "0")}:${String(cfg.schedule_minute || 0).padStart(2, "0")}`}
              onChange={(e) => {
                const [h, m] = e.target.value.split(":").map(Number);
                patchCfg({ schedule_hour: h, schedule_minute: m });
              }}
              className="bg-black border border-white/15 px-3 py-1.5 font-mono text-sm text-white"
              data-testid="guide-schedule-time"
            />
            <label className="font-mono text-[11px] uppercase tracking-widest text-white/60">Timezone</label>
            <input
              defaultValue={cfg.timezone}
              onBlur={(e) => e.target.value !== cfg.timezone && patchCfg({ timezone: e.target.value })}
              className="bg-black border border-white/15 px-3 py-1.5 font-mono text-sm text-white min-w-[180px]"
              data-testid="guide-schedule-tz"
            />
            <label className="font-mono text-[11px] uppercase tracking-widest text-white/60">Monthly cap $</label>
            <input
              type="number"
              defaultValue={cfg.monthly_cap_usd}
              min="1" step="1"
              onBlur={(e) => Number(e.target.value) !== cfg.monthly_cap_usd && patchCfg({ monthly_cap_usd: Number(e.target.value) })}
              className="bg-black border border-white/15 px-3 py-1.5 font-mono text-sm text-white w-24"
              data-testid="guide-cap"
            />
          </div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            cron is pinned at /app/.emergent/crons.yml · updating this form only re-schedules the agent{"'"}s internal logic, not the platform cron fire time
          </p>
        </div>
      </section>

      {/* Narration voices */}
      <section className="mb-10">
        <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-white/70 mb-3">// Narration voices</h2>
        <div className="border border-white/10 p-5 space-y-4">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <button
              type="button"
              data-testid="guide-alternate-voices"
              onClick={() => patchCfg({ alternate_voices: !cfg.alternate_voices })}
              disabled={working || !(cfg.eleven_voices || []).length}
              className={`relative h-6 w-11 rounded-full transition-colors ${cfg.alternate_voices ? "bg-[#39FF14]" : "bg-white/15"} disabled:opacity-40`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-black transition-all ${cfg.alternate_voices ? "left-[22px]" : "left-0.5"}`} />
            </button>
            <span className="font-mono text-[11px] uppercase tracking-widest text-white/70">
              Alternate voices per video
            </span>
          </label>
          {(cfg.eleven_voices || []).length === 0 ? (
            <p className="font-mono text-xs text-white/40">
              No voice rotation set. The agent uses the single configured voice
              {cfg.eleven_voice_label ? ` (${cfg.eleven_voice_label})` : ""}.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(cfg.eleven_voices || []).map((v, i) => {
                const isNext = cfg.alternate_voices &&
                  i === ((cfg.voice_cursor || 0) % (cfg.eleven_voices.length || 1));
                return (
                  <div
                    key={v.id}
                    data-testid={`guide-voice-${i}`}
                    className={`flex items-center gap-2 px-3 py-2 border font-mono text-xs ${isNext ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/15 text-white/70"}`}
                  >
                    <Mic className="h-3 w-3" />
                    <span>{v.label || v.id}</span>
                    {isNext && <span className="text-[9px] uppercase tracking-widest opacity-80">next</span>}
                  </div>
                );
              })}
            </div>
          )}
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            {cfg.alternate_voices
              ? "videos rotate through these voices for variety"
              : "turn on alternation to rotate voices per published video"}
          </p>
        </div>
      </section>

      {/* Generate now */}
      <section className="mb-10">
        <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-white/70 mb-3">// Trigger now</h2>
        <div className="border border-white/10 p-5 flex flex-wrap gap-3">
          <button
            data-testid="guide-generate-tutorial"
            onClick={() => generateNow("tutorial")}
            disabled={working || cfg.paused}
            className="px-5 py-3 bg-[#00FFFF]/10 border border-[#00FFFF] text-[#00FFFF] font-mono text-xs uppercase tracking-widest hover:bg-[#00FFFF]/20 transition-all disabled:opacity-40"
          >
            <Zap className="h-3 w-3 inline mr-2" />Generate Tutorial video
          </button>
          <button
            data-testid="guide-generate-earning"
            onClick={() => generateNow("earning")}
            disabled={working || cfg.paused}
            className="px-5 py-3 bg-[#FFD700]/10 border border-[#FFD700] text-[#FFD700] font-mono text-xs uppercase tracking-widest hover:bg-[#FFD700]/20 transition-all disabled:opacity-40"
          >
            <DollarSign className="h-3 w-3 inline mr-2" />Generate Earning explainer
          </button>
          <button
            onClick={loadAll}
            className="px-5 py-3 border border-white/15 text-white/70 font-mono text-xs uppercase tracking-widest hover:border-white/30"
            data-testid="guide-refresh"
          >
            <RefreshCcw className="h-3 w-3 inline mr-2" />Refresh
          </button>
        </div>
      </section>

      {/* Screen library */}
      <section className="mb-10">
        <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-white/70 mb-3">// Screenshot library ({screens.length})</h2>
        <div className="border border-white/10 p-5">
          <div className="flex items-center gap-3 mb-4">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => uploadScreen(e.target.files?.[0])}
              className="hidden"
              data-testid="guide-screen-file"
            />
            <button
              data-testid="guide-screen-upload"
              onClick={() => fileRef.current?.click()}
              className="px-4 py-2 border border-[#39FF14] text-[#39FF14] font-mono text-xs uppercase tracking-widest hover:bg-[#39FF14]/10"
            >
              <Upload className="h-3 w-3 inline mr-2" />Upload screen
            </button>
            <p className="text-white/40 text-xs">Upload at least 4 vertical (9:16) app screenshots. The agent picks from these to build each video.</p>
          </div>
          {screens.length === 0 ? (
            <p className="font-mono text-xs text-[#FF007F]">No screens yet — the agent cannot render without at least one.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {screens.map((s) => (
                <div key={s.id} data-testid={`guide-screen-${s.id}`} className="relative group border border-white/10 bg-black">
                  <img src={`${API}${s.preview_url}`} alt={s.label} className="w-full aspect-[9/16] object-cover" />
                  <div className="absolute inset-x-0 bottom-0 bg-black/70 p-1 text-[10px] text-white truncate">{s.label || "screen"}</div>
                  <button
                    onClick={() => deleteScreen(s.id)}
                    data-testid={`guide-screen-delete-${s.id}`}
                    className="absolute top-1 right-1 bg-black/70 border border-[#FF007F]/50 text-[#FF007F] p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Approval queue */}
      <section className="mb-10">
        <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-white/70 mb-3">// Approval queue ({jobs.length})</h2>
        <div className="space-y-3">
          {jobs.length === 0 && <p className="font-mono text-xs text-white/40">No jobs yet. Trigger one above or wait for the next scheduled fire.</p>}
          {jobs.map((j) => (
            <JobCard key={j.id} job={j} onApprove={approve} onReject={reject} />
          ))}
        </div>
      </section>

      {/* Activity log */}
      <section>
        <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-white/70 mb-3">// Activity log</h2>
        <div className="border border-white/10 bg-black/30 p-4 max-h-80 overflow-y-auto font-mono text-[11px] space-y-1">
          {logs.length === 0 && <p className="text-white/40">No activity yet.</p>}
          {logs.map((l) => (
            <div key={l.id} className="flex gap-3">
              <span className="text-white/30 shrink-0">{new Date(l.created_at).toLocaleString()}</span>
              <span className={l.level === "error" ? "text-[#FF007F]" : "text-white/80"}>{l.message}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Card({ icon: Icon, label, value, sub, color, pct }) {
  const c = color || "#39FF14";
  return (
    <div className="border border-white/10 p-4 bg-black/30">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-3 w-3" style={{ color: c }} />
        <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">{label}</span>
      </div>
      <p className="font-display text-2xl font-bold" style={{ color: c }}>{value}</p>
      {sub && <p className="font-mono text-[10px] text-white/40 mt-1">{sub}</p>}
      {pct != null && (
        <div className="mt-2 h-1 bg-white/10">
          <div className="h-1" style={{ width: `${pct}%`, background: pct > 85 ? "#FF007F" : c }} />
        </div>
      )}
    </div>
  );
}

function JobCard({ job, onApprove, onReject }) {
  const [showScript, setShowScript] = useState(false);
  const stateColors = {
    queued: "#FFD700", scripting: "#FFD700", narrating: "#00FFFF",
    rendering: "#00FFFF", ready_for_approval: "#39FF14",
    published: "#39FF14", failed: "#FF007F", rejected: "#808080",
  };
  const c = stateColors[job.status] || "#808080";
  return (
    <div data-testid={`guide-job-${job.id}`} className="border border-white/10 p-4 bg-black/30">
      <div className="flex items-start gap-4">
        {job.thumb_url ? (
          <img src={`${API}${job.thumb_url}`} alt="" className="h-24 w-14 object-cover border border-white/10" />
        ) : (
          <div className="h-24 w-14 border border-white/10 bg-white/5 flex items-center justify-center">
            <ImageIcon className="h-5 w-5 text-white/30" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border" style={{ borderColor: c, color: c }}>
              {job.status.replace(/_/g, " ")}
            </span>
            <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">{job.kind} · {new Date(job.created_at).toLocaleString()}</span>
            {job.cost_usd != null && <span className="font-mono text-[9px] text-white/40">${job.cost_usd.toFixed(3)}</span>}
          </div>
          <p className="font-semibold text-sm truncate">{job.topic_title || "(pending)"}</p>
          {job.script && (
            <button
              onClick={() => setShowScript((v) => !v)}
              className="font-mono text-[10px] uppercase tracking-widest text-[#00FFFF] hover:underline mt-1"
              data-testid={`guide-job-script-${job.id}`}
            >
              <FileText className="h-3 w-3 inline mr-1" />{showScript ? "hide" : "show"} script
            </button>
          )}
          {job.error && <p className="font-mono text-xs text-[#FF007F] mt-2">{job.error}</p>}
          {showScript && job.script && (
            <div className="mt-3 p-3 bg-black border border-white/10 text-xs">
              <p className="font-bold mb-1">{job.script.title}</p>
              <p className="italic text-white/70 mb-2">{job.script.hook}</p>
              {(job.script.body_lines || []).map((l, i) => <p key={i} className="text-white/80">• {l}</p>)}
              <p className="mt-2 text-[#39FF14]">{job.script.cta}</p>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {job.preview_url && (
            <a
              href={`${API}${job.preview_url}`}
              target="_blank" rel="noreferrer"
              className="px-3 py-1.5 border border-white/15 text-white/70 font-mono text-[10px] uppercase tracking-widest hover:border-white/30 text-center"
              data-testid={`guide-job-preview-${job.id}`}
            >
              <Mic className="h-3 w-3 inline mr-1" />Preview
            </a>
          )}
          {job.status === "ready_for_approval" && (
            <>
              <button
                onClick={() => onApprove(job.id)}
                className="px-3 py-1.5 border border-[#39FF14] text-[#39FF14] font-mono text-[10px] uppercase tracking-widest hover:bg-[#39FF14]/10"
                data-testid={`guide-job-approve-${job.id}`}
              >
                <Check className="h-3 w-3 inline mr-1" />Approve
              </button>
              <button
                onClick={() => onReject(job.id)}
                className="px-3 py-1.5 border border-[#FF007F] text-[#FF007F] font-mono text-[10px] uppercase tracking-widest hover:bg-[#FF007F]/10"
                data-testid={`guide-job-reject-${job.id}`}
              >
                <X className="h-3 w-3 inline mr-1" />Reject
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
